import React from 'react';
import { Box, Container, Typography, Grid, Stack, Divider, Link, Avatar, IconButton, Tooltip } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DiamondIcon from '@mui/icons-material/Diamond';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';

// Premium Color Palette (Matching Navbar and Home Page)
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  white: '#ffffff',
  textLight: 'rgba(255,255,255,0.7)',
  textLighter: 'rgba(255,255,255,0.5)',
};

const Footer = () => {
  const offices = [
    {
      country: "United States",
      address: ["Suite 1200 1000 N. West Street,", "Downtown Wilmington,", "Delaware,", "19801"],
      phone: "+1-302-295-3810"
    },
    {
      country: "South Africa",
      address: ["341 Beyers Naude Drive,", "Northcliff Johannesburg Drive,", "South Africa,", "2195"],
      phone: "+27 72 010 0332"
    }
  ];

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Products", path: "/products" },
    { name: "Application", path: "/application" },
    { name: "Careers", path: "/careers" },
    { name: "News", path: "/news" },
    { name: "Contact", path: "/contact" },
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, url: "https://facebook.com/diamondcard", label: "Facebook" },
    { icon: <TwitterIcon />, url: "https://twitter.com/diamondcard", label: "Twitter" },
    { icon: <LinkedInIcon />, url: "https://linkedin.com/company/diamondcard", label: "LinkedIn" },
    { icon: <InstagramIcon />, url: "https://instagram.com/diamondcard", label: "Instagram" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: colors.navy,
        color: colors.white,
        py: 5,
        mt: 'auto',
        borderTop: `1px solid ${colors.gold}33`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={5}>
          {/* Logo and Tagline */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card Logo"
                sx={{ 
                  width: 55, 
                  height: 55, 
                  mr: 1.5,
                  backgroundColor: 'transparent',
                  '& img': {
                    objectFit: 'contain',
                  }
                }}
                variant="square"
              />
              <Typography variant="h5" sx={{ color: colors.gold, fontWeight: 800, letterSpacing: 1.5 }}>
                Diamond Card
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: colors.textLight, lineHeight: 1.6 }}>
              The first choice alternate bank. Be your own private bank with complete financial freedom.
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textLighter, fontStyle: 'italic' }}>
              THE SAFE BANK ®
            </Typography>
          </Grid>

          {/* US Office */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.gold, mb: 2, letterSpacing: 1 }}>
              United States
            </Typography>
            <Box sx={{ display: 'flex', mb: 1.5 }}>
              <LocationOnIcon sx={{ fontSize: 18, mr: 1.5, color: colors.gold, mt: 0.5 }} />
              <Box>
                {offices[0].address.map((line, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: colors.textLight, lineHeight: 1.6, mb: 0.5 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <PhoneIcon sx={{ fontSize: 16, mr: 1.5, color: colors.gold }} />
              <Typography variant="body2" sx={{ color: colors.textLight }}>
                {offices[0].phone}
              </Typography>
            </Box>
          </Grid>

          {/* South Africa Office */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.gold, mb: 2, letterSpacing: 1 }}>
              South Africa
            </Typography>
            <Box sx={{ display: 'flex', mb: 1.5 }}>
              <LocationOnIcon sx={{ fontSize: 18, mr: 1.5, color: colors.gold, mt: 0.5 }} />
              <Box>
                {offices[1].address.map((line, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: colors.textLight, lineHeight: 1.6, mb: 0.5 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <PhoneIcon sx={{ fontSize: 16, mr: 1.5, color: colors.gold }} />
              <Typography variant="body2" sx={{ color: colors.textLight }}>
                {offices[1].phone}
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.gold, mb: 2, letterSpacing: 1 }}>
              Quick Links
            </Typography>
            <Stack spacing={1.5}>
              {quickLinks.map((link, idx) => (
                <Link 
                  key={idx}
                  href={link.path}
                  sx={{ 
                    color: colors.textLight, 
                    textDecoration: 'none', 
                    fontSize: '0.85rem',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      color: colors.gold, 
                      transform: 'translateX(5px)' 
                    }
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact & Social */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.gold, mb: 2, letterSpacing: 1 }}>
              Connect
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LanguageIcon sx={{ fontSize: 16, mr: 1.5, color: colors.gold }} />
              <Link 
                href="https://www.diamondcard.com" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: colors.textLight, textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: colors.gold } }}
              >
                www.diamondcard.com
              </Link>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ fontSize: 16, mr: 1.5, color: colors.gold }} />
              <Link 
                href="mailto:info@diamondcard.com"
                sx={{ color: colors.textLight, textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: colors.gold } }}
              >
                info@diamondcard.com
              </Link>
            </Box>
            
            {/* Social Icons */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
              {socialLinks.map((social, idx) => (
                <Tooltip title={social.label} key={idx}>
                  <IconButton
                    component={Link}
                    href={social.url}
                    target="_blank"
                    sx={{ 
                      color: colors.textLight,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      width: 35,
                      height: 35,
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        color: colors.navy,
                        backgroundColor: colors.gold,
                        transform: 'translateY(-3px)',
                      }
                    }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, bgcolor: `${colors.gold}33` }} />

        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems="center" 
          spacing={2}
        >
          <Typography variant="caption" sx={{ color: colors.textLighter }}>
            © {new Date().getFullYear()} Diamond Card Corporation. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography variant="caption" sx={{ color: colors.textLighter }}>
              Privacy Policy
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textLighter }}>
              Terms of Service
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textLighter }}>
              Cookie Policy
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: colors.textLighter }}>
            Member of the Diamond Network
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;